import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bot,
  BookMarked,
  BookOpen,
  Code2,
  Edit3,
  FileText,
  Globe,
  GraduationCap,
  Heart,
  Package,
  Sparkles,
  Users,
} from 'lucide-react';
import type { ItemTipo } from '@central/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useCreateItem } from '@/hooks/useItems';
import { cn } from '@/lib/utils';

const TIPOS: Array<{
  tipo: ItemTipo;
  label: string;
  descricao: string;
  icon: typeof Code2;
}> = [
  { tipo: 'app-proprio', label: 'App Próprio', descricao: 'Um app web ou serviço que você usa', icon: Code2 },
  { tipo: 'app-aluno', label: 'App Aluno', descricao: 'Um app construído pra mentorada', icon: Heart },
  { tipo: 'agente-autonomo', label: 'Agente Autônomo', descricao: 'Executa sob demanda', icon: Bot },
  { tipo: 'agente-loop', label: 'Agente Loop', descricao: 'Recorrente (cron)', icon: Bot },
  { tipo: 'dominio', label: 'Domínio', descricao: 'Subdomínio ou domínio', icon: Globe },
  { tipo: 'mentorada', label: 'Mentorada', descricao: 'Cliente ativa', icon: Users },
  { tipo: 'projeto', label: 'Projeto', descricao: 'Projeto em andamento', icon: Package },
  { tipo: 'curso', label: 'Curso', descricao: 'Curso estruturado', icon: GraduationCap },
  { tipo: 'aula', label: 'Aula', descricao: 'Aula avulsa', icon: BookOpen },
  { tipo: 'doc', label: 'Doc', descricao: 'Documento', icon: FileText },
  { tipo: 'termo-glossario', label: 'Termo Glossário', descricao: 'Verbete técnico', icon: BookMarked },
  { tipo: 'lousa', label: 'Lousa', descricao: 'Quadro de desenho', icon: Edit3 },
];

interface AddItemModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddItemModal({ open, onOpenChange }: AddItemModalProps): JSX.Element {
  const [step, setStep] = useState<'tipo' | 'form'>('tipo');
  const [tipoSelecionado, setTipoSelecionado] = useState<ItemTipo | null>(null);
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [url, setUrl] = useState('');
  const [tagsStr, setTagsStr] = useState('');
  const [error, setError] = useState<string | null>(null);
  const create = useCreateItem();
  const navigate = useNavigate();

  function reset(): void {
    setStep('tipo');
    setTipoSelecionado(null);
    setNome('');
    setDescricao('');
    setUrl('');
    setTagsStr('');
    setError(null);
  }

  function handleClose(): void {
    reset();
    onOpenChange(false);
  }

  function handleSelectTipo(tipo: ItemTipo): void {
    setTipoSelecionado(tipo);
    setStep('form');
  }

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!tipoSelecionado) return;
    setError(null);

    const tags = tagsStr
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);

    try {
      const item = await create.mutateAsync({
        tipo: tipoSelecionado,
        nome,
        descricao: descricao || undefined,
        url: url || undefined,
        tags: tags.length > 0 ? tags : undefined,
      });
      handleClose();
      navigate(`/items/${item.id}`);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  const meta = tipoSelecionado ? TIPOS.find((t) => t.tipo === tipoSelecionado) : null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-2xl">
        {step === 'tipo' ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="h-4 w-4" /> O que você quer adicionar?
              </DialogTitle>
              <DialogDescription>Escolha o tipo do item. Você pode reclassificar depois.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-3 gap-2 max-h-[420px] overflow-auto pr-1">
              {TIPOS.map((t) => (
                <button
                  type="button"
                  key={t.tipo}
                  onClick={() => handleSelectTipo(t.tipo)}
                  className={cn(
                    'flex flex-col items-start gap-1 p-3 rounded-md border border-border text-left transition-colors',
                    'hover:bg-accent hover:border-primary/40',
                  )}
                >
                  <t.icon className="h-4 w-4 text-muted-foreground" />
                  <div className="font-medium text-sm">{t.label}</div>
                  <div className="text-xs text-muted-foreground leading-tight">{t.descricao}</div>
                </button>
              ))}
            </div>
          </>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {meta && <meta.icon className="h-4 w-4" />} Novo {meta?.label}
              </DialogTitle>
              <DialogDescription>Campos obrigatórios marcados com *.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label htmlFor="nome" className="block text-sm font-medium mb-1">Nome *</label>
                <Input
                  id="nome"
                  required
                  autoFocus
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  placeholder="Ex: Escritório Império"
                />
              </div>
              <div>
                <label htmlFor="url" className="block text-sm font-medium mb-1">URL</label>
                <Input
                  id="url"
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://..."
                />
              </div>
              <div>
                <label htmlFor="descricao" className="block text-sm font-medium mb-1">Descrição</label>
                <Textarea
                  id="descricao"
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  placeholder="Descrição curta — o que é, pra que serve"
                  rows={3}
                />
              </div>
              <div>
                <label htmlFor="tags" className="block text-sm font-medium mb-1">Tags</label>
                <Input
                  id="tags"
                  value={tagsStr}
                  onChange={(e) => setTagsStr(e.target.value)}
                  placeholder="Ex: nextjs, producao, ana (separadas por vírgula)"
                />
              </div>
              {error && (
                <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md px-3 py-2">
                  {error}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setStep('tipo')}>
                Voltar
              </Button>
              <Button type="submit" disabled={create.isPending || !nome}>
                {create.isPending ? 'Criando…' : 'Criar'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
